import { LinkedinSubTypeEnum } from '../../../Common/Basic/enums/linkedin-sub-type.enum';
import { TwitterSubTypeEnum } from '../../../Common/Basic/enums/twitter-sub-type.enum';
import { BlueskySubTypeEnum } from '../../../Common/Basic/enums/bluesky-sub-type.enum';
import { BroadcastSubTypeEnum } from '../../../Common/Basic/enums/broadcast-sub-type.enum';
import { FilterOptionModel } from '../../../Common/Filter/models/filter-option.model';
import {
    MediaTypeEnum,
    mediaTypeNoHeadlineMap,
    mediaTypeIconMap,
    socialMediaTypeImageMap,
    SocialMediaType,
    getSocialMediaTypes,
    getSocialMediaPlatformTypes,
    getSavedSearchMediaTypes
} from '../../../Common/Basic/enums/media-type.enum';

export class MediaTypeModel {
    id: MediaTypeEnum;
    childId: string;
    name: string = '';
    tooltip: string;
    icon: string;
    noHeadlineMessage: string;
    isSocial: boolean;
    isSavedSearch: boolean;
    isProvisioned: boolean;
    isDisabled?: boolean;
    socialIconImageUrl: string;

    children: FilterOptionModel<MediaTypeModel>[];

    constructor(
        id: MediaTypeEnum,
        childId?: string,
        isDisabled: boolean = false,
    ) {
        this.id = id;
        this.childId = childId;
        this.icon = mediaTypeIconMap.get(id);
        this.socialIconImageUrl = socialMediaTypeImageMap.get(id as SocialMediaType);
        this.isSocial = getSocialMediaPlatformTypes().includes(id);
        this.isSavedSearch = getSavedSearchMediaTypes().includes(id);
        this.noHeadlineMessage = mediaTypeNoHeadlineMap.get(id);
        this.isDisabled = isDisabled;

        const nameId = this.childId !== undefined ? this.childId : this.id;
        this.name = `MediaType.${nameId}`;
        this.tooltip = `MediaType.${nameId}`;
    }

    static getAllMediaTypes(): MediaTypeModel[] {
        return Object.values(MediaTypeEnum)
            .map((mediaType) => new MediaTypeModel(mediaType));
    }

    static getAllMediaTypesExceptTikTok(): MediaTypeModel[] {
        return Object.values(MediaTypeEnum)
            .filter(mediaType => mediaType !== MediaTypeEnum.TIKTOK)
            .map((mediaType) => new MediaTypeModel(mediaType));
    }

    static getAllTwitterSubTypes(): MediaTypeModel[] {
        return Object.values(TwitterSubTypeEnum)
            .map((subType) => new MediaTypeModel(MediaTypeEnum.TWITTER, subType));
    }

    static getAllLinkedinSubTypes(): MediaTypeModel[] {
        return Object.values(LinkedinSubTypeEnum)
            .map((subType) => new MediaTypeModel(MediaTypeEnum.LINKEDIN, subType));
    }

    static getAllBlueskySubTypes(): MediaTypeModel[] {
        return Object.values(BlueskySubTypeEnum)
            .map((subType) => new MediaTypeModel(MediaTypeEnum.BLUESKY, subType));
    }

    static getSocialMediaTypes(): MediaTypeModel[] {
        return getSocialMediaTypes().map(x => new MediaTypeModel(x));
    }

    static getTvSubTypes(broadcastTypes?: BroadcastSubTypeEnum[]): MediaTypeModel[] {
        return broadcastTypes?.length
            ? broadcastTypes.map(subType => new MediaTypeModel(MediaTypeEnum.TV, subType))
            : Object.values(BroadcastSubTypeEnum).map((subType) => new MediaTypeModel(MediaTypeEnum.TV, subType));
    }

    /**
     * True when TV is included with no Programs/Advertisements sub type selected.
     */
    static hasMissingBroadcastSubType(mediaTypes: MediaTypeModel[]): boolean {
        return (mediaTypes || []).some(mediaType => mediaType.id === MediaTypeEnum.TV && !mediaType.children?.length);
    }

    /**
     * Returns true if the provided mediaTypeModel is of type "broadcast"
     */
    isBroadCastMediaType(): boolean {
        if (
            this.id === MediaTypeEnum.TV
            || this.id === MediaTypeEnum.RADIO
        ) {
            return true;
        }

        return false;
    }

    clone(): MediaTypeModel {
        const clone = new MediaTypeModel(this.id, undefined, this.isDisabled);
        clone.isProvisioned = this.isProvisioned;

        return clone;
    }

    equals(mediaType: MediaTypeModel): boolean {
        if (this.id !== mediaType?.id) {
            return false;
        }
        if (this.name !== mediaType?.name) {
            return false;
        }
        if (this.tooltip !== mediaType?.tooltip) {
            return false;
        }
        if (this.icon !== mediaType?.icon) {
            return false;
        }
        if (this.noHeadlineMessage !== mediaType?.noHeadlineMessage) {
            return false;
        }
        if (this.isSocial !== mediaType?.isSocial) {
            return false;
        }
        if (this.isProvisioned !== mediaType?.isProvisioned) {
            return false;
        }

        return true;
    }
}
